/* eslint-disable prefer-const */
import { useEffect, useState } from 'react';
import Select from 'react-select';
import { TreeSelect } from 'antd';
import { useTranslation } from 'react-i18next';
import Loading from '@components/Shared/Loader/Loading';
import ToastSwal from '@components/Shared/Alert/ToastSwal';
import useGetConsolidationSet from '@hooks/Finance/ConsolidationSet/useGetConsolidationSet';
import { Card, CardBody, CardHeader } from '@components/card/card';
import useGlChartLookups from '@hooks/Finance/ChartAccountsDetails/useGlChartLookups';

interface Props {
  onAccountSelect: (account: any) => void;
  accountDetails: any;
  organizationId: string;
  distributedFlag: boolean;
}

const CodeCombination = ({ onAccountSelect, accountDetails, organizationId, distributedFlag }: Props) => {
  const { t, i18n } = useTranslation();
  const { GlCompanyId, GetGlBrancheId, loading: loading2, error: error2 } = useGetConsolidationSet();

  const { TreeCostCenters, OptionsCompanies, OptionsBranches, OptionsAccounts, loading, error } = useGlChartLookups();

  function findAccountNumberById(id: any) {
    function search(data: any, id: any) {
      for (let item of data) {
        if (item.value === id) {
          return item.accountNumber;
        }
        if (item.children && item.children?.length > 0) {
          const result: any = search(item.children, id);
          if (result) {
            return result;
          }
        }
      }
      return null; // Return null if not found
    }

    return search(TreeCostCenters, id); // Use TreeCostCenters directly
  }

  const GlCompany = OptionsCompanies?.filter(f => f.value === GlCompanyId);
  const GlBranch = OptionsBranches?.filter(f => f.value === GetGlBrancheId(organizationId));

  const [selectedAccounts, setSelectedAccounts] = useState({
    company: {
      id: accountDetails?.companyId ?? null,
      accountNumber: OptionsCompanies?.find(f => f.value === accountDetails?.companyId)?.accountNumber ?? ''
    },
    branch: {
      id: accountDetails?.branchId ?? null,
      accountNumber: OptionsBranches?.find(f => f.value === accountDetails?.branchId)?.accountNumber ?? ''
    },

    costCenter: {
      id: accountDetails?.costCenterId ?? null,
      accountNumber: findAccountNumberById(accountDetails?.costCenterId) ?? ''
    },

    account: {
      id: accountDetails?.accountId ?? null,
      accountNumber: OptionsAccounts?.find(f => f.value === accountDetails?.accountId)?.accountNumber ?? ''
    }
  });

  const [selectedAccountDisplay, setSelectedAccountDisplay] = useState('');

  useEffect(() => {
    // تحديث بيانات الشركة
    const company = GlCompany[0];
    if (company?.value && !selectedAccounts.company.id) {
      setSelectedAccounts(prevState => ({
        ...prevState,
        company: {
          id: company.value ?? null,
          accountNumber: company.accountNumber ?? ''
        }
      }));
    }

    // تحديث بيانات الفرع
    const branch = GlBranch[0];
    if (branch?.value && !selectedAccounts.branch.id) {
      setSelectedAccounts(prevState => ({
        ...prevState,
        branch: {
          id: branch.value ?? null,
          accountNumber: branch.accountNumber ?? ''
        }
      }));
    }
  }, [GlBranch, GlCompany, selectedAccounts.branch.id, selectedAccounts.company.id]);

  useEffect(() => {
    // تحديث عرض الحساب المختار عند تغيير القيم
    const { company, branch, costCenter, account } = selectedAccounts;

    if (company.accountNumber && branch.accountNumber && costCenter.accountNumber && account.accountNumber) {
      const formattedAccount = `${company.accountNumber} - ${branch.accountNumber} - ${costCenter.accountNumber} - ${account.accountNumber}`;
      setSelectedAccountDisplay(formattedAccount);
    } else {
      setSelectedAccountDisplay('');
    }
  }, [selectedAccounts]);

  const handleChange = (name: string, option: any) => {
    if (name === 'costCenter') {
      const accountNumber = findAccountNumberById(option);
      setSelectedAccounts(prevValues => ({
        ...prevValues,
        [name]: { id: option, accountNumber: accountNumber }
      }));
    } else {
      setSelectedAccounts(prevValues => ({
        ...prevValues,
        [name]: { id: option?.value || null, accountNumber: option?.accountNumber || '' }
      }));
    }
  };

  const ChooseAccount = () => {
    const { company, branch, costCenter, account } = selectedAccounts;

    if (!company.id || !branch.id || !costCenter.id || !account.id) {
      ToastSwal({
        title: i18n.language === 'ar' ? 'يرجى إدخال جميع الحقول' : 'Please fill out all fields',
        position: 'top-start'
      });

      return;
    }

    onAccountSelect(selectedAccounts); // Send the selected account back to parent
  };

  return (
    <>
      <Loading loading={loading || loading2} error={error || error2} Type='Dots'>
        <Card>
          <CardHeader className='d-flex align-items-center'>
            <i className='fa-lg fas fa-code me-2 text-theme'></i>
            <h5 className='mb-0'>{t('إختيــار الحســـاب')}</h5>
          </CardHeader>
          <CardBody>
            <div className='row'>
              <div className='col-lg-12 mb-4'>
                <div className='input-group flex-nowrap'>
                  <span className='input-group-text d-flex  align-items-center' style={{ width: '170px' }}>
                    <i className='fas fa-building me-2 text-theme'></i>
                    {t('Company')}
                  </span>
                  <Select
                    isDisabled
                    classNamePrefix='react-select'
                    className='w-100'
                    isLoading={loading}
                    isSearchable={true}
                    options={GlCompany}
                    placeholder={
                      <div>
                        <i className='fas fa-search me-1'></i>
                        {i18n.language === 'ar' ? 'إختر' : 'Choose'}
                      </div>
                    }
                    onChange={option => handleChange('company', option)}
                    value={GlCompany?.find(f => f.value === selectedAccounts.company.id)}
                  />
                </div>
              </div>

              <div className='col-lg-12 mb-4 '>
                <div className='input-group flex-nowrap'>
                  <span className='input-group-text d-flex  align-items-center' style={{ width: '170px' }}>
                    <i className='fas fa-code-branch me-2 text-theme'></i>
                    {t('Branch')}
                  </span>
                  <Select
                    isDisabled={!distributedFlag}
                    // options={GlBranch?.filter(f => f.accountParant === selectedAccounts.company.id)}

                    options={
                      distributedFlag
                        ? OptionsBranches
                        : GlBranch?.filter(f => f.accountParant === selectedAccounts.company.id)
                    }
                    classNamePrefix='react-select'
                    className='w-100'
                    isLoading={loading}
                    isSearchable={true}
                    isClearable
                    name='branchId'
                    placeholder={
                      <div>
                        <i className='fas fa-search me-1'></i>
                        {i18n.language === 'ar' ? 'إختر' : 'Choose'}
                      </div>
                    }
                    onChange={option => handleChange('branch', option)}
                    value={OptionsBranches?.find(f => f.value === selectedAccounts.branch.id)}
                  />
                </div>
              </div>

              <div className='col-lg-12 mb-4 '>
                <div className='input-group flex-nowrap'>
                  <span className='input-group-text d-flex  align-items-center' style={{ width: '170px' }}>
                    <i className='fas fa-sitemap me-2 text-theme'></i>
                    {t('CostCenter')}
                  </span>
                  <TreeSelect
                    showSearch
                    treeData={TreeCostCenters}
                    style={{ borderRadius: '1px !important' }}
                    className='custom-tree-select w-100 rounded-0'
                    treeLine
                    placeholder={
                      <div>
                        <i className='fas fa-search me-1'></i>
                        {i18n.language === 'ar' ? 'إختر' : 'Choose'}
                      </div>
                    }
                    allowClear
                    treeDefaultExpandAll
                    getPopupContainer={triggerNode => triggerNode.parentNode}
                    onChange={option => handleChange('costCenter', option)}
                    value={selectedAccounts.costCenter.id}
                    // suffixIcon={<i className='fas fa-chevron-down text-theme'></i>}
                  />
                </div>
              </div>

              <div className='col-lg-12 mb-4 '>
                <div className='input-group flex-nowrap'>
                  <span className='input-group-text d-flex  align-items-center' style={{ width: '170px' }}>
                    <i className='fas fa-file-invoice-dollar me-2 text-theme'></i>
                    {t('Accounts')}
                  </span>
                  <Select
                    classNamePrefix='react-select'
                    className='w-100'
                    isLoading={loading}
                    isSearchable={true}
                    isClearable
                    name='accountId'
                    options={OptionsAccounts}
                    // placeholder={
                    //   <div>
                    //     <i className='fas fa-search me-1'></i>
                    //     {i18n.language === 'ar' ? 'إختر' : 'Choose'}
                    //   </div>
                    // }
                    placeholder={''}
                    onChange={option => handleChange('account', option)}
                    value={OptionsAccounts?.find(f => f.value === selectedAccounts.account.id)}
                  />
                </div>
              </div>
            </div>

            {/* عرض الحساب المختار */}
            {selectedAccountDisplay && (
              <div className='row mt-2'>
                <h6 className='mb-1'>{t('الحساب المختار')}:</h6>
                <div className='col-lg-12'>
                  <Card className='bg-theme bg-opacity-10'>
                    <div className='card-body '>
                      <div className='d-flex justify-content-center align-items-center'>
                        {/* <i className='fas fa-check-circle text-success me-3 fa-2x'></i> */}
                        <div>
                          <h4 className='text-theme mb-0'>{selectedAccountDisplay}</h4>
                        </div>
                      </div>
                    </div>
                  </Card>
                </div>
              </div>
            )}

            <div className='d-flex justify-content-end align-items-center mt-4 gap-2'>
              <button
                type='button'
                className='btn btn-outline-secondary d-flex align-items-center justify-content-center'
                onClick={() => onAccountSelect(null)}
                disabled={loading || loading2}
                style={{ minWidth: '100px' }}
              >
                <i className='fas fa-times-circle me-2'></i>
                {t('إلغاء')}
              </button>

              <button
                type='button'
                className='btn btn-theme d-flex align-items-center justify-content-center'
                onClick={() => ChooseAccount()}
                disabled={loading || loading2}
                style={{ minWidth: '200px' }}
              >
                {loading || loading2 ? (
                  <>
                    <i className='fas fa-spinner fa-spin me-2'></i>
                    {t('جاري التحميل...')}
                  </>
                ) : (
                  <>
                    <i className='fas fa-check-circle me-2'></i>
                    {t('إختيــــار')}
                  </>
                )}
              </button>
            </div>
          </CardBody>
        </Card>
      </Loading>

      <style>
        {`
        .ant-select-outlined:not(.ant-select-customize-input) .ant-select-selector {
            border: 1px solid var(--bs-border-color);
            border-radius: 3px !important;
            height: 2rem;
        }
          `}
      </style>
    </>
  );
};

export default CodeCombination;
